// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// One sanctioned, guarded parse of the frozen `forge_spec_snapshot_json` blob on
// the cycle detail (fe-dashboard-data-layer; PRD US §7). Mirrors the prior-art
// sub-tree in `src/views/forges/details.tsx` (`spec_json.evaluation_protocol`),
// but on the cycle's frozen snapshot rather than the live forge spec.
//
// INV-7 — payload is dataset-free at source (`to_persistable_spec()` in
// `ebiose-core/ebiose/core/evolution_forge.py` strips evaluator + dataset before
// the wire); this selector additionally whitelists only `n_trials` /
// `timeout_seconds` / `aggregation` — no other sub-tree of the blob (and no
// unknown key inside this one) ever reaches the panel-facing API, so a future
// blob addition cannot leak through by accident.

import { isFiniteNumber } from "./number-guards"

export interface EvaluationProtocol {
  // null when absent, malformed, or the blob has no evaluation_protocol at all
  nTrials: number | null
  timeoutSeconds: number | null
  aggregation: string | null
}

const ABSENT: EvaluationProtocol = {
  nTrials: null,
  timeoutSeconds: null,
  aggregation: null,
}

// Never throws — a missing blob, missing sub-tree, or malformed field degrades
// that field to `null` rather than propagating `undefined` or an unrelated blob
// value onto the panel.
export const parseEvaluationProtocol = (
  forgeSpecSnapshotJson: Record<string, unknown> | null | undefined,
): EvaluationProtocol => {
  const raw = forgeSpecSnapshotJson?.evaluation_protocol
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return ABSENT
  }

  const obj = raw as Record<string, unknown>
  return {
    nTrials: isFiniteNumber(obj.n_trials) ? obj.n_trials : null,
    timeoutSeconds: isFiniteNumber(obj.timeout_seconds) ? obj.timeout_seconds : null,
    aggregation: typeof obj.aggregation === "string" ? obj.aggregation : null,
  }
}
